
import { useState, useEffect, useRef, memo } from 'react'
import { Button} from '@mui/material'
import { jaJP } from '@mui/x-data-grid'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import BackspaceIcon from '@mui/icons-material/Backspace'
import DoneIcon from '@mui/icons-material/Done'
import { useAuth } from '../../contexts/AuthContext'
import { usePost } from '../../apis/ApiRequests'
import { CustomDataGrid } from '../../components/dataGrid/CustomDataGrid'
import CustomSnackBar from '../../components/alert/CustomSnackBar'
import CustomConfirm from '../../components/confirm/CustomConfirm'
import Header from '../../components/Header'
import Frame from '../../components/uiParts/Frame'
import TitleBar from '../../components/uiParts/TitleBar'
import MastaSearchForm from '../../components/uiParts/maintenance/MastaSearchForm'
import CustomOutlinedInput from '../../components/input/CustomOutlinedInput'
import { read, utils } from 'xlsx'

const ShipGroupList = memo(() => {
    
	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - ベース情報を取得
	//-----------------------------------------------
    const { authInfo } = useAuth()
    const facilityCode = authInfo.facilityCode
    const userId       = authInfo.userId
    
	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
    const [ dataGridHeight,    setDataGridHeight    ] = useState(window.innerHeight - 250)
    const [ condition ,        setCondition         ] = useState('')
    const [ shipGroupList,     setShipGroupList     ] = useState([])
    const [ searchCount,       setSearchCount       ] = useState(0)
    const [ targetList,        setTargetList        ] = useState('')
    const [ openDialog,        setOpenDialog        ] = useState(false)
    const [ requireMessage,    setRequireMessage    ] = useState('')
    const [excelDataList,      setExcelDataList     ] = useState([])
    const [ excelImportFlag,   setExcelImportFlag   ] = useState(false)
    const [ fileName,          setFileName          ] = useState('')
    
    // 登録データ
    const [registerData, setRegisterData] = useState({
        code: '',
        name: '',
        displayName: '',
        displayNo: '',
        nameKana: '',
    })
    // UI Message
    const [messageState, setMessageState] = useState({
        snackBarTrue  : false,
        snackBarFalse : false,
        require       : false,
        confirm       : false,
        confirm_excel : false
    })
    
	//-----------------------------------------------
    // initialize - useRef
	//-----------------------------------------------
    const effectRan = useRef(false)
    const fileInputRef = useRef()
    
	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
    // API - searchData / masta_ShipGroup
	//-----------------------------------------------
    const { response : searchData_Res, loading : searchData_Loading, post:searchData_Post } = usePost()
    const searchData = async () => {
        const postData = {
            'className'    : 'mch.models.masta.ShipGroup',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'keyword1'     : condition
        }
        await searchData_Post(postData)
    }
	//-----------------------------------------------
    // API - setData / masta_ShipGroup
	//-----------------------------------------------
    const { response : setData_Res, loading : setData_Loading, post:setData_Post } = usePost()
    const setData = async (targetData) => {
        const postData = {
            'className'    : 'mch.models.masta.ShipGroup',
            'methodName'   : 'setData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'activeFlag'   : targetData.activeFlag,
            'code'         : targetData.code,
            'name'         : targetData.name,
            'displayName'  : targetData.displayName,
            'nameKana'     : targetData.nameKana,
            'displayNo'    : targetData.displayNo,
        }
        await setData_Post(postData)
    }
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {
        // 初回マウント時のみ実行
        if (effectRan.current === false) {
            effectRan.current = true
            
            setSearchCount(searchCount+1)
            
            const handleResize = () => {
              setDataGridHeight(window.innerHeight - 250)
            }
            window.addEventListener('resize', handleResize)
            return () => {
                window.removeEventListener('resize', handleResize)
            }
        }
    },[])
	//-----------------------------------------------
    // useEffect - searchData / masta_ShipGroup
	//-----------------------------------------------
    useEffect(() => {
        setShipGroupList([])
        if (searchData_Res) {
            setShipGroupList(searchData_Res.Data)
        }
    }, [searchData_Res])
	//-----------------------------------------------
    // useEffect - setData / masta_ShipGroup
	//-----------------------------------------------
    useEffect(() => {
        if (setData_Res) {
            setOpenDialog(false)
            setMessageInfo('confirm', false)
            setMessageInfo('snackBarTrue', true)
            setSearchCount(searchCount+1)
        }
    }, [setData_Res])
	//-----------------------------------------------
    // useEffect - 検索処理
	//-----------------------------------------------
    useEffect(() => {
        searchData()
    }, [searchCount])
	//-----------------------------------------------
    // useEffect - dataGirdの選択
	//-----------------------------------------------
    useEffect(() => {
        if (targetList) {
            setRegisterData({
                'code'        : targetList.code,
                'name'        : targetList.name,
                'displayName' : targetList.displayName,
                'nameKana'    : targetList.nameKana,
                'displayNo'   : targetList.displayNo
            })
            setOpenDialog(true) // ダイアログを表示
        }
    }, [targetList])
	//-----------------------------------------------
    // useEffect - エクセルファイル取り込み処理
	//-----------------------------------------------
    useEffect(() => {
        if (excelImportFlag) {
            
            excelDataList.map((item) => {
                const targetData = { ...item, 'activeFlag':1}
                setData(targetData)
            })
            setExcelImportFlag(false)
            setMessageInfo('confirm_excel', false)
        }
    }, [excelDataList, excelImportFlag])

	//===============================================
	// eventHandler
	//===============================================
	//-----------------------------------------------
    // eventHandler - 検索ボタン
	//-----------------------------------------------
    const handleClick_search = () => {
        setSearchCount(searchCount+1)
    }
	//-----------------------------------------------
    // eventHandler - 検索クリアボタン
	//-----------------------------------------------
    const handleClick_search_clear = () => {
        setCondition('')
        setSearchCount(searchCount+1)
    }
	//-----------------------------------------------
    // eventHandler - 新規ボタン
	//-----------------------------------------------
    const handleClick_new = () => {
        setRegisterData({
            'code'        : '',
            'name'        : '',
            'displayName' : '',
            'nameKana'    : '',
            'displayNo'   : ''
        })
        setOpenDialog(true)
    }
	//-----------------------------------------------
    // eventHandler - データの設定
	//-----------------------------------------------
    const handleChange = (name, value) => {
        setRegisterData(prevSearchData => ({
            ...prevSearchData,
            [name]: value,
        }))
    }
	//-----------------------------------------------
    // eventHandler - 登録・削除ボタン
	//-----------------------------------------------
    const handleClick_Register = (activeFlag) => {
        const requireStatus = checkedRequire() // 必須項目をチェックする
        if (requireStatus) {
            const targetData = { ...registerData, activeFlag}
            setData(targetData)
        } else {
            setMessageInfo('require', true)
        }
    }
	//-----------------------------------------------
    // eventHandler - ダイアログを非表示
	//-----------------------------------------------
    const handleClick_dialog_close = () => {
        setOpenDialog(false)
    }
	//-----------------------------------------------
    // eventHandler - EXCELファイルの取り込み
	//-----------------------------------------------
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFileName(file.name);
    
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = read(data, { type: 'array' });
    
                const sheetName = '荷主グループ'; // 読み込むシート名
                const worksheet = workbook.Sheets[sheetName];
    
                if (!worksheet) {
                    setRequireMessage("指定されたシート名が存在しません。");
                    setMessageInfo('require', true);
                    return;
                }
    
                // シートからデータをJSON形式で取得
                const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
                const rows = jsonData.map((row, index) => {
                    if (index === 0) return null; // ヘッダー行をスキップ
                    return {
                        code: row[0] || '',
                        name: row[1] || '',
                        displayName: row[2] || '',
                        nameKana: row[3] || '',
                        displayNo: row[4] || ''
                    };
                }).filter(row => row !== null);
    
                setMessageInfo('confirm_excel', true);
                setExcelDataList(rows);
            };
            reader.readAsArrayBuffer(file);
        }
    }
	//-----------------------------------------------
    // eventHandler - EXCELファイルの参照
	//-----------------------------------------------
    const handeliClick_excel = () => {
        fileInputRef.current.click()
    }
    const handliClick_excelImportOK = () => {
        setExcelImportFlag(true)
    }
    //===============================================
    // dataGird
    //===============================================
    //-----------------------------------------------
    // dataGird - テーブルの見出し
    //-----------------------------------------------
    const columns = [
        { field: 'displayNo',   headerName: '表示No',     width: 100, columnSeparatorWidth: 1, type:'number', },
        { field: 'code',        headerName: 'コード',  width: 200, columnSeparatorWidth: 1, },
        { field: 'name',        headerName: '名称',    width: 300, columnSeparatorWidth: 1, },
        { field: 'displayName', headerName: '表示名称', width: 300, columnSeparatorWidth: 1, },
        { field: 'nameKana',    headerName: 'カナ名称', width: 300, columnSeparatorWidth: 1, },
    ]
    //-----------------------------------------------
    // dataGrid - 行の選択
    //-----------------------------------------------
    const handleClick_dataGrid = (event) => {
        setTargetList(event.row)
    }
	//===============================================
	// Function
	//===============================================
	//-----------------------------------------------
	// Function - 必須項目をチェックする
	//-----------------------------------------------
    const checkedRequire = () => {
        const errorFields = []
    
        if (!registerData.code) errorFields.push('「コード」')
        if (!registerData.name) errorFields.push('「名称」')
        if (!registerData.displayName) errorFields.push('「表示名称」')
        if (!registerData.displayNo) errorFields.push('「表示No」')
    
        if (errorFields.length) {
            const text = errorFields.join('、') + 'が入力されていません'
            setRequireMessage(text);
            return false;  // エラーがある場合は false を返します
        }
        
        return true  // エラーがない場合は true を返します
    }
	//===============================================
	// UI Message
	//===============================================
    const setMessageInfo = (target, state) => {
        setMessageState(prevSearchData => ({
            ...prevSearchData,
            [target]: state,
        }))
    }
    //===============================================
    // Css
    //===============================================
    const dialog_button_close = { 
        fontSize: '16px', height:'40px', width:'320px', color:'#fff', backgroundColor:'#808080'
    }
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/bidMastaMenu" />
            <div className='shipGroupList bodyContens'>
                <TitleBar title="荷主グループマスタ一覧" />
                <div className='mainContents'>
                    <Frame classname="frame" title="マスタ検索">
                        <MastaSearchForm
                            id                = "shipGroupList_condition"
                            value             = { condition }
                            handleChange      = { (e) => setCondition(e.target.value) }
                            handleSearch      = { () => handleClick_search() }
                            handleSearchClear = { () => handleClick_search_clear() }
                            handleClickNew    = { () => handleClick_new() }
                            excelRef            = { fileInputRef }
                            handleExcelOnchange = { (e) => handleFileChange(e) }
                            handleExcelClick    = { (e) => handeliClick_excel(e) }
                        />
                    </Frame>
                    <Frame classname="frame" title="マスタ一覧">
                        <CustomDataGrid
                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                            }
                            editMode                = "row"
                            rows                    = { shipGroupList }
                            columns                 = { columns }
                            sx                      = { {height:dataGridHeight,fontSize:14,borderColor: 'primary.light',} }
                            density                 = "compact"
                            localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                            showColumnRightBorder
                            showCellRightBorder
                            initialState            = {{pagination: { paginationModel: { pageSize: 100 } },}}
                            pageSizeOptions         = {[50, 100, 150, 200]}
                            loading                 = { searchData_Loading }
                            onCellClick             = {(event) => handleClick_dataGrid(event)}
                        />
                    </Frame>
                </div>
            </div>
            <div className='shipGroupList_dialog'>
                <Dialog open={ openDialog } onClose={ handleClick_dialog_close } maxWidth='lg' >
                    <DialogTitle>
                        <TitleBar title="荷主マスタ登録" />
                    </DialogTitle>
                    <DialogContent style={{border:'none'}} sx = {{width:'auto'}}>
                        <div style={{backgroundColor:'#efefef', padding:'10px'}}>
                            <div>
                                <CustomOutlinedInput
                                    id       = "shipGroupList_code"
                                    label    = "コード"
                                    value    = { registerData.code }
                                    width    = "300px"
                                    onChange = {(e) => handleChange('code', e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "shipGroupList_name"
                                    label    = "名称"
                                    value    = { registerData.name }
                                    width    = "300px"
                                    onChange = {(e) => handleChange('name', e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "shipGroupList_displayName"
                                    label    = "表示名称"
                                    value    = { registerData.displayName }
                                    width    = "300px"
                                    onChange = {(e) => handleChange('displayName', e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "shipGroupList_nameKana"
                                    label    = "カナ名称"
                                    value    = { registerData.nameKana }
                                    width    = "300px"
                                    onChange = {(e) => handleChange('nameKana', e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "shipGroupList_displayNo"
                                    label    = "表示No"
                                    value    = { registerData.displayNo }
                                    width    = "300px"
                                    onChange = {(e) => handleChange('displayNo', e.target.value)}
                                />
                            </div>
                            <div style={{display:'flex', marginRight:'10px',marginTop:'15px'}}>
                                <div style={{marginLeft:'5px'}}>
                                    <Button
                                        color     = "attention"
                                        size      = "large"
                                        variant   = "contained"
                                        sx        = {{width:'140px', color:'#fff'}}
                                        onClick   = { () => setMessageInfo('confirm', true) }
                                        startIcon = {<BackspaceIcon />}
                                    >{'削除'}
                                    </Button>
                                    
                                </div>
                                <div>
                                    <Button
                                        color     = "primary"
                                        size      = "large"
                                        variant   = "contained"
                                        sx        = {{width:'140px', marginLeft:'10px'}}
                                        onClick   = { () => handleClick_Register(1) }
                                        startIcon = {<DoneIcon />}
                                    >{'登録'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div style={{marginTop:'20px'}}>
                            <Button
                                color     = "tertiary"
                                size      = "large"
                                variant   = "contained"
                                sx        = { dialog_button_close }
                                onClick   = {() => handleClick_dialog_close()}
                                startIcon = {<CloseIcon />}
                            >{'閉じる'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
                <CustomSnackBar
                    open            = {messageState.snackBarTrue}
                    onClose         = {() => setMessageInfo('snackBarTrue', false)}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "登録しました"
                />
                <CustomSnackBar
                    open            = {messageState.snackBarFalse}
                    onClose         = {() => setMessageInfo('snackBarFalse', false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = "登録に失敗しました"
                />
                <CustomSnackBar
                    open            = {messageState.require}
                    onClose         = {() => setMessageInfo('require', false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = {requireMessage}
                />
                <CustomConfirm
                    open      = {messageState.confirm}
                    onClose   = {() => setMessageInfo('confirm', false)}
                    onClickNG = {() => setMessageInfo('confirm', false)}
                    onClickOK = {() => handleClick_Register(0)}
                    message   = "指定データの削除を行います。よろしいですか？"
                />
                <CustomConfirm
                    open      = {messageState.confirm_excel}
                    onClose   = {() => setMessageInfo('confirm_excel', false)}
                    onClickNG = {() => setMessageInfo('confirm_excel', false)}
                    onClickOK = {() => handliClick_excelImportOK()}
                    message   = "エクセルファイルのアップロードを行います。よろしいですか？"
                />
            </div>
        </>
    )
})
export default ShipGroupList

  